// External libraries
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button, buttonVariants } from '@/components/ui/button'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { useLogsSearchParams } from '@/hooks/monitor/useLogsSearchParams'
import useCourses from '@/hooks/courses/useCourses'

import { cn } from '@/lib/utils'

import { Search, PlusCircle, CheckIcon } from 'lucide-react'
import { toast } from 'sonner'
import { CalendarDatePicker } from '../ui/calendar-date-picker'

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${month}-${day}-${year}`
}

const fallbackDate = formatDate(new Date())

export const formSchema = z.object({
  courseName: z.string().min(9, 'Course name is required'),
  date: z.object({
    from: z.date({ required_error: 'Date is required' }),
    to: z.date({ required_error: 'Date is required' }),
  }),
})

export default function LogsSearchForm() {
  const {
    data: coursesList,
    isLoading: isCoursesLoading,
    isPending: isCoursesPending,
    error: courseListError,
  } = useCourses()
  const { courseName, date, setSearchParams } = useLogsSearchParams()

  if (courseListError) {
    toast.error(courseListError.message)
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      courseName: courseName ?? '',
      date: date
        ? {
            from: new Date(date),
            to: new Date(date),
          }
        : {
            from: new Date(fallbackDate),
            to: new Date(fallbackDate),
          },
    },
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    // console.log('values', values)
    const formattedDate = formatDate(values.date.from)
    setSearchParams({ courseName: values.courseName, date: formattedDate })

    // form.reset()
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8">
        <div className="flex flex-col w-full sm:w-3/4 sm:flex-row justify-between mx-auto items-stretch gap-3">
          <FormField
            control={form.control}
            name="courseName"
            render={({ field }) => (
              <FormItem className="w-full sm:w-[45%] space-y-0">
                <FormLabel htmlFor="course-name"></FormLabel>
                <FormControl className="h-11">
                  <Popover>
                    <PopoverTrigger
                      className={`${buttonVariants({ variant: 'outline', size: 'lg', className: 'w-full' })}`}
                    >
                      {!field.value && <PlusCircle className="size-5 text-primary pr-1" />}
                      {field.value ? <span className="truncate w-full">{field.value}</span> : 'Select course'}
                    </PopoverTrigger>
                    <PopoverContent align="start">
                      <Command>
                        <CommandInput placeholder="Enter course name" />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup heading="Courses">
                            {coursesList?.map((course) => (
                              <CommandItem
                                disabled={isCoursesLoading || isCoursesPending}
                                onSelect={() => {
                                  setSearchParams({
                                    courseName: course.courseName,
                                  })
                                  field.onChange({ target: { value: course.courseName } })
                                }}
                                key={course.courseName}
                                value={course.courseName}
                              >
                                <div
                                  className={cn(
                                    'mr-2 flex size-4 items-center justify-center rounded-sm border border-muted-foreground',
                                    course.courseName === courseName
                                      ? 'bg-muted-foreground text-background'
                                      : 'opacity-50 [&_svg]:invisible',
                                  )}
                                >
                                  <CheckIcon className={cn('size-4')} />
                                </div>
                                {course.courseName}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          <CommandSeparator />
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormControl>
                {/* <FormDescription>Course name must be 3 letters followed by 3 digits.</FormDescription> */}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="w-full sm:w-[45%] space-y-0">
                <FormLabel htmlFor="date"></FormLabel>
                <FormControl className="h-11">
                  <CalendarDatePicker
                    date={field.value}
                    onDateSelect={({ from, to }) => {
                      form.setValue('date', { from, to })
                    }}
                    variant="outline"
                    numberOfMonths={1}
                    className="w-full h-full"
                  />
                </FormControl>
                {/* <FormDescription>Select the date for the course.</FormDescription> */}
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="h-11">
            <Button
              type="submit"
              size="icon"
              className={cn('h-full w-full flex-grow flex')}
              disabled={isCoursesLoading || isCoursesPending}
            >
              <Search className="size-5 sm:w-20" />
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
